import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, User, Linkedin, Globe, FileText, Award, AlertCircle, CheckCircle } from "lucide-react";
import { ExpertApplicationData, certificationOptions } from "./schema";
import { toast } from "sonner";

interface PersonalInfoStepProps {
  form: UseFormReturn<ExpertApplicationData>;
  onNext: () => void;
  onFileUpload: (file: File, type: 'profile' | 'cv') => Promise<void>;
}

export function PersonalInfoStep({ form, onNext, onFileUpload }: PersonalInfoStepProps) {
  const { register, formState: { errors }, watch, setValue, getValues } = form;
  const [selectedCertifications, setSelectedCertifications] = React.useState<string[]>([]);

  const handleCertificationChange = (certification: string, checked: boolean) => {
    const updated = checked 
      ? [...selectedCertifications, certification]
      : selectedCertifications.filter(c => c !== certification);
    
    setSelectedCertifications(updated);
    setValue("certifications", updated);
  };

  // Function to get validation status for this step
  const getStepValidationStatus = () => {
    const formData = getValues();
    const missingFields: string[] = [];
    const completedFields: string[] = [];

    // Check each mandatory field for this step
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      missingFields.push("Full Name");
    } else {
      completedFields.push("Full Name");
    }

    if (!formData.email || !formData.email.includes('@')) {
      missingFields.push("Email Address");
    } else {
      completedFields.push("Email Address");
    }

    if (!formData.phone || formData.phone.trim().length === 0) {
      missingFields.push("Phone Number");
    } else {
      completedFields.push("Phone Number");
    }

    if (!formData.profileImage || formData.profileImage.trim().length === 0) {
      missingFields.push("Profile Image");
    } else {
      completedFields.push("Profile Image");
    }

    if (!formData.professionalTitle || formData.professionalTitle.trim().length < 2) {
      missingFields.push("Professional Title");
    } else {
      completedFields.push("Professional Title");
    }

    if (!formData.yearsExperience || formData.yearsExperience < 1) {
      missingFields.push("Years of Experience");
    } else {
      completedFields.push("Years of Experience");
    }

    if (!formData.certifications || formData.certifications.length === 0) {
      missingFields.push("At least one Professional Certification");
    } else {
      completedFields.push(`${formData.certifications.length} Certification(s)`);
    }

    return { missingFields, completedFields };
  };

  const { missingFields, completedFields } = getStepValidationStatus();
  const isStepComplete = missingFields.length === 0;

  const handleCVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a PDF, DOC, or DOCX file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('CV file must be less than 10MB');
        return;
      }
      
      await onFileUpload(file, 'cv');
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Validation Summary */}
      {!isStepComplete && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <div className="font-medium mb-2">Complete these items to continue:</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {missingFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {isStepComplete && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="font-medium mb-2">✅ All required fields completed!</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {completedFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Image Upload */}
      <div className="flex flex-col items-center space-y-4">
        <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
          {watch('profileImage') ? (
            <img 
              src={watch('profileImage')} 
              alt="Profile" 
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <User className="w-8 h-8 text-gray-400" />
          )}
        </div>
        <div>
          <Label htmlFor="profileImage" className="cursor-pointer">
            <div className="flex items-center space-x-2 text-sm text-grandpal-green hover:text-grandpal-green/80">
              <Upload className="w-4 h-4" />
              <span>Upload Profile Photo *</span>
            </div>
          </Label>
          <input
            id="profileImage"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                // Validate file type
                if (!file.type.startsWith('image/')) {
                  toast.error('Please select a valid image file');
                  return;
                }
                
                // Validate file size (max 5MB)
                if (file.size > 5 * 1024 * 1024) {
                  toast.error('Image file must be less than 5MB');
                  return;
                }
                
                onFileUpload(file, 'profile');
              }
            }}
          />
          <p className="text-xs text-gray-500 mt-1">Required - helps families connect with you</p>
          {errors.profileImage && (
            <p className="text-red-600 text-xs mt-1">{errors.profileImage.message}</p>
          )}
        </div>
      </div>

      {/* Personal Information */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="fullName">Full Name *</Label>
          <Input 
            id="fullName"
            {...register("fullName")}
            placeholder="Your full name"
          />
          {errors.fullName && (
            <p className="text-red-600 text-sm mt-1">{errors.fullName.message}</p>
          )}
        </div>
        
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input 
            id="email"
            type="email"
            {...register("email")}
            placeholder="your.email@example.com"
          />
          {errors.email && (
            <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone">Phone Number *</Label>
          <Input 
            id="phone"
            {...register("phone")}
            placeholder="+353 XX XXX XXXX"
          />
          <p className="text-xs text-gray-500 mt-1">Required for contact and verification</p>
          {errors.phone && (
            <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
          )}
        </div>
        
        <div>
          <Label htmlFor="professionalTitle">Professional Title *</Label>
          <Input 
            id="professionalTitle"
            {...register("professionalTitle")}
            placeholder="e.g., Fair Deal Specialist, Care Advisor"
          />
          {errors.professionalTitle && (
            <p className="text-red-600 text-sm mt-1">{errors.professionalTitle.message}</p>
          )}
        </div>
      </div>

      {/* Professional Links */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="linkedinUrl" className="flex items-center gap-2">
            <Linkedin className="w-4 h-4" />
            LinkedIn Profile
          </Label>
          <Input 
            id="linkedinUrl"
            {...register("linkedinUrl")}
            placeholder="https://linkedin.com/in/yourprofile"
          />
          <p className="text-xs text-gray-500 mt-1">Optional - helps with professional verification</p>
          {errors.linkedinUrl && (
            <p className="text-red-600 text-sm mt-1">{errors.linkedinUrl.message}</p>
          )}
        </div>
        
        <div>
          <Label htmlFor="websiteUrl" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Personal Website
          </Label>
          <Input 
            id="websiteUrl"
            {...register("websiteUrl")}
            placeholder="https://yourwebsite.com"
          />
          <p className="text-xs text-gray-500 mt-1">Optional - helps showcase your work</p>
          {errors.websiteUrl && (
            <p className="text-red-600 text-sm mt-1">{errors.websiteUrl.message}</p>
          )}
        </div>
      </div>

      {/* Experience */}
      <div>
        <Label htmlFor="yearsExperience">Years of Experience *</Label>
        <Input 
          id="yearsExperience"
          type="number"
          min="1"
          {...register("yearsExperience", { valueAsNumber: true })}
          placeholder="5"
        />
        <p className="text-xs text-gray-500 mt-1">Most experts have 5-15 years of relevant experience</p>
        {errors.yearsExperience && (
          <p className="text-red-600 text-sm mt-1">{errors.yearsExperience.message}</p>
        )}
      </div>

      {/* Certifications */}
      <div>
        <Label className="flex items-center gap-2">
          <Award className="w-4 h-4" />
          Professional Certifications *
        </Label>
        <p className="text-sm text-gray-600 mb-3">Select all that apply - at least one certification is required</p>
        <div className="grid md:grid-cols-2 gap-2">
          {certificationOptions.map((certification) => (
            <div key={certification.id} className="flex items-center space-x-2">
              <Checkbox
                id={certification.id}
                checked={selectedCertifications.includes(certification.id)}
                onCheckedChange={(checked) => 
                  handleCertificationChange(certification.id, checked as boolean)
                }
              />
              <Label htmlFor={certification.id} className="text-sm">
                {certification.label}
              </Label>
            </div>
          ))}
        </div>
        {errors.certifications && (
          <p className="text-red-600 text-sm mt-1">{errors.certifications.message}</p>
        )}
      </div>

      {/* CV Upload */}
      <div>
        <Label htmlFor="cvFile" className="flex items-center gap-2">
          <FileText className="w-4 h-4" />
          CV/Resume Upload
        </Label>
        <div className="mt-2">
          <input
            id="cvFile"
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={handleCVUpload}
          />
          <Label htmlFor="cvFile" className="cursor-pointer">
            <div className="flex items-center space-x-2 text-sm text-grandpal-green hover:text-grandpal-green/80 border border-dashed border-gray-300 rounded-lg p-3">
              <Upload className="w-4 h-4" />
              <span>Upload CV/Resume (PDF, DOC, DOCX)</span>
            </div>
          </Label>
          <p className="text-xs text-gray-500 mt-1">Optional - helps verify your experience and qualifications</p>
          {watch('cvFile') && (
            <p className="text-xs text-green-600 mt-1">✓ CV uploaded successfully</p>
          )}
          {errors.cvFile && (
            <p className="text-red-600 text-xs mt-1">{errors.cvFile.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button 
          type="button" 
          onClick={onNext} 
          className="bg-grandpal-green hover:bg-grandpal-green/90"
          disabled={!isStepComplete}
        >
          Continue to Specializations
        </Button>
      </div>
    </div>
  );
}
